import {
  type FileCoverage,
  type StudyFileMediaType,
  type StudyFileMeta,
  BYTES_PER_MEBIBYTE,
  MAX_PDF_PAGES_READ,
} from '@sigora/domain';

/** What each stored type is called on screen. */
const TYPE_NAMES: Record<StudyFileMediaType, string> = {
  'application/pdf': 'PDF',
  'text/plain': 'Text',
  'image/png': 'PNG image',
  'image/jpeg': 'JPEG image',
  'image/webp': 'WebP image',
};

/** The extensions a name may end with for each stored type. */
const TYPE_EXTENSIONS: Record<StudyFileMediaType, readonly string[]> = {
  'application/pdf': ['pdf'],
  'text/plain': ['txt', 'md'],
  'image/png': ['png'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/webp': ['webp'],
};

/** What the file input offers to pick: every extension a stored type may carry. */
export const MATERIALS_FILE_ACCEPT = Object.values(TYPE_EXTENSIONS)
  .flat()
  .map((extension) => `.${extension}`)
  .join(',');

/** A byte count the way a person reads one: bytes, then whole KiB, then MiB to one place. */
export const fileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${String(bytes)} ${bytes === 1 ? 'byte' : 'bytes'}`;
  }

  if (bytes < BYTES_PER_MEBIBYTE) {
    return `${String(Math.round(bytes / 1024))} KiB`;
  }

  return `${(bytes / BYTES_PER_MEBIBYTE).toFixed(1)} MiB`;
};

/** The file as Sigora found it: its type, its size and, for an image, its pixel size. */
export const fileSummary = (file: StudyFileMeta): string => {
  const parts = [TYPE_NAMES[file.mediaType], fileSize(file.sizeBytes)];
  if (file.width !== null && file.height !== null) {
    parts.push(`${String(file.width)} × ${String(file.height)}`);
  }

  return parts.join(' · ');
};

/**
 * What to say when a file's name suggests one type and its bytes are another, or null when they
 * agree. The bytes decide what a file is kept as, so the screen says what Sigora found rather than
 * leaving the name to imply something else.
 */
export const typeFoundNote = (file: StudyFileMeta): string | null => {
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();

  return TYPE_EXTENSIONS[file.mediaType].includes(extension)
    ? null
    : `Sigora found this is a ${TYPE_NAMES[file.mediaType]}, whatever its name says.`;
};

/** Items as words: "1–2, 4 and 6". */
const listOf = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;

const runWords = (runs: readonly (readonly [number, number])[]): string[] =>
  runs.map(([first, last]) =>
    first === last ? String(first) : `${String(first)}–${String(last)}`,
  );

/** Page runs as words: `[[1, 2], [4, 4]]` is "1–2 and 4". */
const pageList = (runs: readonly (readonly [number, number])[]): string => listOf(runWords(runs));

const pageCount = (runs: readonly (readonly [number, number])[]): number =>
  runs.reduce((sum, [first, last]) => sum + last - first + 1, 0);

/** The pages from `first` to `last` that none of `runs` covers, as runs of their own. */
const gapsBetween = (
  runs: readonly (readonly [number, number])[],
  first: number,
  last: number,
): [number, number][] => {
  const gaps: [number, number][] = [];
  let next = first;
  for (const [start, end] of runs) {
    if (start > next && next <= last) {
      gaps.push([next, Math.min(start - 1, last)]);
    }

    next = Math.max(next, end + 1);
  }

  if (next <= last) {
    gaps.push([next, last]);
  }

  return gaps;
};

const PASSWORD_LINE =
  'Sigora couldn’t open this PDF because it is password-protected. Remove the password and upload it again.';
const DAMAGED_LINE = 'Sigora couldn’t open this file. It may be damaged. Try exporting it again.';
const SCAN_LINE =
  'Sigora found no text in this PDF. It may be a scan. Paste the text or upload a PDF with selectable text.';
const TEXT_LIMIT_SENTENCE = 'The rest didn’t fit in one reading.';
const IMAGE_TOO_LARGE_LINE =
  'Sigora couldn’t send this image with the others in one reading. Remove an image or use a smaller one.';

/** What a PDF's page coverage says: which pages were read, and why each other page was not. */
const pagesLine = (
  coverage: FileCoverage,
  total: number,
  read: [number, number][],
  partPage: number | null,
): string => {
  if (coverage.outcome === 'read') {
    return total === 1 ? 'Read it all.' : `Read all ${String(total)} pages.`;
  }

  if (read.length === 0 && partPage === null) {
    return coverage.reasons.includes('no_text')
      ? SCAN_LINE
      : `Sigora read none of its ${String(total)} pages. They didn’t fit in one reading.`;
  }

  const items = [
    ...runWords(read),
    ...(partPage === null ? [] : [`part of page ${String(partPage)}`]),
  ];
  const prefix = read.length === 0 ? '' : `${pageCount(read) === 1 ? 'page' : 'pages'} `;
  const sentences = [`Read ${prefix}${listOf(items)} of ${String(total)}.`];

  // Past the page limit nothing is read, so a page there was skipped for the limit. Below it, a
  // page after the last one read stopped the reading when the text ran out of room, and any other
  // page left out had no text to read.
  // A page read in part is where the room ran out, so the pages before it are the ones to account
  // for.
  const lastRead = partPage === null ? (read[read.length - 1]?.[1] ?? 0) : partPage - 1;
  const limit = Math.min(total, MAX_PDF_PAGES_READ);
  const textLimited = coverage.reasons.includes('text_limit');
  const blank = gapsBetween(read, 1, textLimited ? lastRead : limit);
  if (coverage.reasons.includes('no_text') && blank.length > 0) {
    sentences.push(
      pageCount(blank) === 1
        ? `Page ${pageList(blank)} has no text Sigora can read.`
        : `Pages ${pageList(blank)} have no text Sigora can read.`,
    );
  }

  if (textLimited) {
    sentences.push(TEXT_LIMIT_SENTENCE);
  }

  if (coverage.reasons.includes('page_limit')) {
    sentences.push(`Sigora reads the first ${String(MAX_PDF_PAGES_READ)} pages.`);
  }

  return sentences.join(' ');
};

/**
 * What one file's reading covered, in a sentence or two: all of it, which pages, or why nothing
 * could be read. It never says more was read than the coverage records.
 */
export const coverageLine = (coverage: FileCoverage): string => {
  if (coverage.reasons.includes('image_too_large')) {
    return IMAGE_TOO_LARGE_LINE;
  }

  if (coverage.reasons.includes('password_protected')) {
    return PASSWORD_LINE;
  }

  if (coverage.reasons.includes('damaged')) {
    return DAMAGED_LINE;
  }

  if (coverage.pages !== null) {
    return pagesLine(
      coverage,
      coverage.pages.total,
      coverage.pages.read.map(([first, last]): [number, number] => [first, last]),
      coverage.pages.part?.page ?? null,
    );
  }

  if (coverage.outcome === 'read') {
    return 'Read it all.';
  }

  if (coverage.characters !== null && coverage.characters.read > 0) {
    return `Read part of it. ${TEXT_LIMIT_SENTENCE}`;
  }

  return coverage.reasons.includes('text_limit')
    ? 'Sigora didn’t read this file. It didn’t fit in one reading.'
    : 'Sigora couldn’t read this file.';
};
